import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-800 w-full">
      {/* Hero Section */}
      <section className="relative flex flex-col items-center justify-center flex-grow text-center px-6 py-24 overflow-hidden">
        <img
          src="/squash-hero.png"
          alt="Squash court"
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        />
        <div className="relative z-10 max-w-3xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-extrabold mb-6 text-blue-700"
          >
            Welcome to <span className="text-blue-500">Squash Hub</span>
          </motion.h1>
          <p className="text-lg md:text-xl text-gray-700 mb-8">
            Where passion meets precision. Join our growing squash community — train, compete,
            and track your progress all in one place.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate("/register")}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-full shadow-md transition"
            >
              Get Started
            </button>
            <button
              onClick={() => navigate("/about-us")}
              className="border border-blue-500 text-blue-500 hover:bg-blue-50 font-semibold px-8 py-3 rounded-full transition"
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-6 md:px-20 bg-gray-50">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-10">
          <img
            src="/squash-court.png"
            alt="About Squash"
            className="rounded-2xl shadow-lg w-full md:w-1/2 object-cover"
          />
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold text-blue-600 mb-4">
              What is Squash Hub?
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Squash Hub is your all-in-one digital companion for squash training and
              performance tracking. From attendance logs and PT notes to detailed reviews
              and periodisation plans — everything you need as a player or coach in one
              seamless platform.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 md:px-20">
        <h2 className="text-center text-4xl font-bold mb-12 text-blue-600">
          Why Choose Us
        </h2>
        <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
          {[
            {
              title: "Track Performance",
              desc: "View progress reports, skill reviews, and fitness records tailored for each athlete.",
              icon: "📊",
            },
            {
              title: "Coach Collaboration",
              desc: "Seamless coach-player interaction for training logs, attendance, and planning.",
              icon: "🤝",
            },
            {
              title: "Data-Driven Insights",
              desc: "Make informed decisions with visual dashboards and analytics powered by your activity data.",
              icon: "💡",
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.2 }}
              className="bg-white border border-gray-200 p-8 rounded-2xl shadow-sm hover:shadow-lg transition"
            >
              <div className="text-5xl mb-4">{item.icon}</div>
              <h3 className="text-2xl font-semibold text-blue-600 mb-2">
                {item.title}
              </h3>
              <p className="text-gray-600">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-50 text-center">
        <h2 className="text-3xl font-bold text-blue-600 mb-4">
          Ready to Elevate Your Game?
        </h2>
        <p className="text-gray-700 mb-8 mx-4">
          Join us today and experience smarter training and performance management.
        </p>
        <button
          onClick={() => navigate("/register")}
          className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-10 py-3 rounded-full shadow-md transition"
        >
          Get Started
        </button>
      </section>
    </div>
  );
}
